import React, { useState, useEffect, useRef } from 'react';
import Webcam from 'react-webcam';
import axios from 'axios';
import { useFacePose } from './useFacePose';
import { useAutoCapture } from './useAutoCapture';
import { uploadPhotoCapture, declarePhotoEra } from './uploadCapture';
import PoseOverlay from './PoseOverlay';
import StatusBadge from './StatusBadge';
import { SELFIE_PROMPTS } from './overlayConfig';

const STAGES = [
  { id: 'Historical', name: 'Historical Photos', min: 5, type: 'upload' },
  { id: 'Present_Neutral', name: 'Present Neutral', min: 6, type: 'camera' },
  { id: 'Pose_Variation_A', name: 'Pose Variation A', min: 4, type: 'camera' },
  { id: 'Pose_Variation_B', name: 'Pose Variation B', min: 4, type: 'camera' }
];

export default function CaptureShell() {
  const [session, setSession] = useState(null);
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [currentPoseNum, setCurrentPoseNum] = useState(1);
  const [photosByStage, setPhotosByStage] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [consentAgreed, setConsentAgreed] = useState(false);

  // Participant Form
  const [participantId, setParticipantId] = useState('');
  const [dobYear, setDobYear] = useState('1998');
  const [dobMonth, setDobMonth] = useState('06');
  const [country, setCountry] = useState('IN');
  const [gender, setGender] = useState('Male');
  const [ethnicity, setEthnicity] = useState('south_asian');
  const [deviceOs, setDeviceOs] = useState('Android');

  // Modal for era declaration (for scanned historical photos without EXIF)
  const [eraModalPhoto, setEraModalPhoto] = useState(null);
  const [selectedEra, setSelectedEra] = useState('2010s');

  const webcamRef = useRef(null);
  const activeStage = STAGES[activeStageIndex];

  const { poseState, detectInVideo } = useFacePose();

  // Face tracker detection loop on webcam frame
  useEffect(() => {
    if (activeStage.type !== 'camera' || !session) return;

    let isMounted = true;
    const interval = setInterval(() => {
      if (!isMounted) return;
      if (webcamRef.current && webcamRef.current.video && webcamRef.current.video.readyState === 4) {
        detectInVideo(webcamRef.current.video, activeStage.id, currentPoseNum);
      }
    }, 150);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [activeStage, currentPoseNum, session, detectInVideo]);

  const onCaptureResult = async (captureData) => {
    if (!session) return;
    setUploadError(null);
    setLoading(true);

    const poseNum = activeStage.id === 'Pose_Variation_A' || activeStage.id === 'Pose_Variation_B'
      ? currentPoseNum
      : (activeStage.id === 'Present_Neutral' ? (photosByStage['Present_Neutral']?.length || 0) + 1 : null);

    const result = await uploadPhotoCapture({
      sessionId: session.id,
      stage: activeStage.id,
      poseNumber: poseNum,
      blobOrFile: captureData.blob,
      meta: {
        width: captureData.width,
        height: captureData.height,
        capturedAt: captureData.capturedAt
      }
    });

    setLoading(false);

    if (!result.success && result.reason) {
      setUploadError(result.reason);
    } else {
      if (activeStage.id === 'Pose_Variation_A' && currentPoseNum < 4) {
        setCurrentPoseNum(currentPoseNum + 1);
      } else if (activeStage.id === 'Pose_Variation_B' && currentPoseNum < 8) {
        setCurrentPoseNum(currentPoseNum + 1);
      }
      refreshSession();
    }
  };

  const { state: overlayState, holdProgress, qualityWarning, triggerManualCapture } = useAutoCapture({
    isAligned: poseState.isAligned,
    faceDetected: poseState.faceDetected,
    faceBox: poseState.faceBox,
    videoRef: webcamRef,
    onCaptureResult,
    enabled: activeStage.type === 'camera' && !loading && !!session
  });

  const refreshSession = async () => {
    if (!session) return;
    try {
      const resp = await axios.get(`/api/capture/session/${session.id}`);
      const data = resp.data;
      const grouped = {};
      (data.photos || []).forEach(p => {
        if (!grouped[p.stage]) grouped[p.stage] = [];
        grouped[p.stage].push(p);
      });
      setPhotosByStage(grouped);
    } catch (e) {
      console.error("Failed to refresh session", e);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!participantId.trim()) return;

    try {
      setLoading(true);
      const resp = await axios.post('/api/capture/session', {
        participant_id: participantId.trim(),
        dob_year: parseInt(dobYear),
        dob_month: parseInt(dobMonth),
        country_iso2: country,
        gender,
        ethnicity,
        device_os: deviceOs,
        consent_version: 'v1.0',
        consent_hash: 'sha256_participant_consent'
      });
      setSession(resp.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      alert(err.response?.data?.error || "Error initializing session");
    }
  };

  const handleHistoricalFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !session) return;

    setLoading(true);
    setUploadError(null);

    for (const f of files) {
      const res = await uploadPhotoCapture({
        sessionId: session.id,
        stage: 'Historical',
        poseNumber: null,
        blobOrFile: f,
        meta: {}
      });
      if (!res.success && res.reason) {
        setUploadError(res.reason);
      }
    }
    setLoading(false);
    refreshSession();
  };

  const handleDeclareEraSubmit = async () => {
    if (!eraModalPhoto || !session) return;
    try {
      await declarePhotoEra(session.id, eraModalPhoto.id, selectedEra);
      setEraModalPhoto(null);
      refreshSession();
    } catch (err) {
      alert("Failed to declare era");
    }
  };

  if (!session) {
    return (
      <div style={{ maxWidth: '600px', margin: '3rem auto', padding: '2rem', background: '#ffffff', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Participant Onboarding</h2>
        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>Enter the Telus participant folder code and metadata to begin verification.</p>

        <form onSubmit={handleCreateSession} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Participant ID (Folder Name)</label>
            <input
              type="text"
              required
              placeholder="e.g. PARTICIPANT_001"
              value={participantId}
              onChange={e => setParticipantId(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1rem', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Birth Year</label>
              <input
                type="number"
                value={dobYear}
                onChange={e => setDobYear(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Birth Month</label>
              <select
                value={dobMonth}
                onChange={e => setDobMonth(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                    {String(i + 1).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Country ISO2</label>
              <input
                type="text"
                maxLength={2}
                value={country}
                onChange={e => setCountry(e.target.value.toUpperCase())}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Gender</label>
              <select
                value={gender}
                onChange={e => setGender(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Ethnicity</label>
              <select
                value={ethnicity}
                onChange={e => setEthnicity(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              >
                <option value="south_asian">South Asian</option>
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="hispanic">Hispanic</option>
                <option value="east_asian">East Asian</option>
                <option value="southeast_asian">Southeast Asian</option>
                <option value="middle_eastern">Middle Eastern</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.35rem' }}>Device OS</label>
              <select
                value={deviceOs}
                onChange={e => setDeviceOs(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
              >
                <option value="Android">Android</option>
                <option value="iOS">iOS</option>
                <option value="Windows">Windows</option>
                <option value="macOS">macOS</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="consent"
              required
              checked={consentAgreed}
              onChange={e => setConsentAgreed(e.target.checked)}
            />
            <label htmlFor="consent" style={{ fontSize: '0.875rem', color: '#475569' }}>
              I consent to the collection and verification of facial biometric photos according to Telus guidelines.
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !consentAgreed}
            style={{
              marginTop: '1rem',
              padding: '0.85rem',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Initializing...' : 'Start Verification Session'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '2rem auto', padding: '1.5rem', background: '#ffffff', borderRadius: '16px' }}>
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Participant: {session.participant_id}</h2>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Telus Dataset Verification Workspace (HD Live Capture)</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <a
            href={`/api/export/${session.id}/telus`}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none', padding: '0.5rem 1rem', background: '#10b981', color: 'white', borderRadius: '6px', fontWeight: 600, fontSize: '0.875rem' }}
          >
            Download Telus ZIP
          </a>
        </div>
      </div>

      {/* Stage tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0' }}>
        {STAGES.map((s, idx) => {
          const count = (photosByStage[s.id] || []).filter(p => p.status === 'accepted').length;
          const isActive = idx === activeStageIndex;
          return (
            <button
              key={s.id}
              onClick={() => { setActiveStageIndex(idx); setCurrentPoseNum(idx === 2 ? 1 : (idx === 3 ? 5 : 1)); }}
              style={{
                padding: '0.75rem 1.25rem',
                border: 'none',
                borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
                background: 'transparent',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? '#2563eb' : '#64748b',
                cursor: 'pointer'
              }}
            >
              {s.name} ({count}/{s.min})
            </button>
          );
        })}
      </div>

      {uploadError && (
        <div style={{ padding: '0.75rem 1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {uploadError}
        </div>
      )}

      {/* Active Stage Body */}
      {activeStage.type === 'camera' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1.5rem' }}>
          {/* Webcam view with 6-layer overlay */}
          <div style={{ position: 'relative', width: '100%', height: '520px', borderRadius: '12px', overflow: 'hidden', background: '#0f172a' }}>
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: { ideal: 2560, min: 1280 },
                height: { ideal: 1920, min: 960 },
                aspectRatio: 4 / 3,
                facingMode: 'user'
              }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <PoseOverlay
              state={overlayState}
              pose={poseState}
              target={activeStage.id}
              poseNumber={currentPoseNum}
              holdProgress={holdProgress}
              qualityWarning={qualityWarning}
              activePromptText={activeStage.id === 'Present_Neutral' ? SELFIE_PROMPTS[Math.min((photosByStage['Present_Neutral'] || []).filter(p => p.status === 'accepted').length, 5)]?.text : null}
            />
          </div>

          {/* Right sidebar: pose selector, selfie variation prompt, and captured list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {activeStage.id === 'Present_Neutral' && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.85rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                  Selfie Variation #{(photosByStage['Present_Neutral'] || []).filter(p => p.status === 'accepted').length + 1} of 6
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#15803d' }}>
                  {SELFIE_PROMPTS[Math.min((photosByStage['Present_Neutral'] || []).filter(p => p.status === 'accepted').length, 5)]?.text}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#166534', marginTop: '0.35rem' }}>
                  Telus requires variance between photos: change clothing, background, or lighting. Photos 4–6 must be taken without glasses.
                </div>
              </div>
            )}

            {(activeStage.id === 'Pose_Variation_A' || activeStage.id === 'Pose_Variation_B') && (
              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>Select Target Pose:</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  {activeStage.id === 'Pose_Variation_A' ? (
                    [
                      { num: 1, label: "1: Look UP" },
                      { num: 2, label: "2: Look DOWN" },
                      { num: 3, label: "3: Look LEFT" },
                      { num: 4, label: "4: Look RIGHT" },
                    ].map(p => (
                      <button
                        key={p.num}
                        onClick={() => setCurrentPoseNum(p.num)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: currentPoseNum === p.num ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          background: currentPoseNum === p.num ? '#eff6ff' : '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: 'pointer'
                        }}
                      >
                        {p.label}
                      </button>
                    ))
                  ) : (
                    [
                      { num: 5, label: "5: UP-LEFT [15°-25°, 20°-35°]" },
                      { num: 6, label: "6: UP-RIGHT [15°-25°, -35°--20°]" },
                      { num: 7, label: "7: DOWN-LEFT [-25°--15°, 20°-35°]" },
                      { num: 8, label: "8: DOWN-RIGHT [-25°--15°, -35°--20°]" },
                    ].map(p => (
                      <button
                        key={p.num}
                        onClick={() => setCurrentPoseNum(p.num)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: currentPoseNum === p.num ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          background: currentPoseNum === p.num ? '#eff6ff' : '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        {p.label}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            <button
              onClick={triggerManualCapture}
              disabled={loading}
              style={{
                padding: '0.75rem',
                background: '#0284c7',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Manual Shutter Capture
            </button>

            {/* Stage Photos Gallery with Honest EXIF Details */}
            <div>
              <h4 style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#475569' }}>Accepted Photos & EXIF Metadata</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '280px', overflowY: 'auto' }}>
                {(photosByStage[activeStage.id] || []).map((p, idx) => (
                  <div key={p.id || idx} style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.6rem', fontSize: '0.8rem', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: 600 }}>#{p.pose_number || idx + 1} ({p.stage})</span>
                      <StatusBadge status={p.status} reason={p.rejection_reason} size="small" />
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      Size: {(p.byte_size / 1024).toFixed(1)} KB • Make: {p.make || 'Imaginary'} • Model: {p.model || 'WebCapture/1.0'}
                    </div>
                    {p.exif_date && (
                      <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '0.15rem' }}>
                        EXIF Date: {p.exif_date.replace('T', ' ').slice(0, 19)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Historical File Upload View */
        <div style={{ padding: '2rem', border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Upload 5+ Historical Photos</h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Original camera EXIF is preserved untouched. Minimum file size 128 KB.
          </p>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleHistoricalFiles}
            disabled={loading}
            style={{ marginBottom: '1.5rem' }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            {(photosByStage['Historical'] || []).map(p => (
              <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem', textAlign: 'left', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Photo #{p.id}</span>
                  <StatusBadge status={p.status} reason={p.rejection_reason} size="small" />
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.3rem' }}>
                  Size: {(p.byte_size / 1024).toFixed(1)} KB
                </div>
                {p.make && (
                  <div style={{ fontSize: '0.72rem', color: '#475569' }}>
                    Camera: {p.make} {p.model || ''}
                  </div>
                )}
                {p.exif_date ? (
                  <div style={{ fontSize: '0.72rem', color: '#16a34a' }}>
                    EXIF: {p.exif_date.slice(0, 10)}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.72rem', color: '#d97706' }}>
                    {p.declared_era ? `Era: ${p.declared_era}` : 'Scanned Print (No EXIF)'}
                  </div>
                )}

                {p.status === 'under_review' && (
                  <button
                    onClick={() => setEraModalPhoto(p)}
                    style={{ marginTop: '0.5rem', fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Declare Era
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Era Declaration Modal */}
      {eraModalPhoto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', width: '360px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>Declare Photo Era</h4>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem' }}>
              This scanned historical print has no digital EXIF date. Please declare the estimated era of the original photo.
            </p>
            <select
              value={selectedEra}
              onChange={e => setSelectedEra(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '1rem' }}
            >
              <option value="1990s">1990s</option>
              <option value="2000s">2000s</option>
              <option value="2010s">2010s</option>
              <option value="pre-1990">Pre-1990</option>
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => setEraModalPhoto(null)} style={{ padding: '0.4rem 0.8rem', border: 'none', background: '#e2e8f0', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeclareEraSubmit} style={{ padding: '0.4rem 0.8rem', border: 'none', background: '#2563eb', color: 'white', borderRadius: '4px', cursor: 'pointer' }}>Confirm Era</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
